import { createContext, useContext, useState, useEffect, useRef } from "react";
import api from '../services/api';
import tokenStorage from './tokenStorage';
import { getFirstAllowedPath, resolvePermissions } from '../config/tenantPermissions';

const AuthContext = createContext();

const INACTIVITY_TIMEOUT = 15 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => tokenStorage.getUser());
  const [cargando, setCargando] = useState(true);
  const refreshTimerRef = useRef(null);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleTokenRefresh = (expiresInSeconds) => {
    clearRefreshTimer();
    const delayMs = Math.max((expiresInSeconds - 60) * 1000, 0);
    refreshTimerRef.current = setTimeout(() => refreshAccessToken(true), delayMs);
  };

  const scheduleRefreshFromStorage = () => {
    const expiresAt = tokenStorage.getTokenExpiresAt();
    if (expiresAt > Date.now()) {
      scheduleTokenRefresh(Math.ceil((expiresAt - Date.now()) / 1000));
    }
  };

  const isUserInactive = () => {
    const last = tokenStorage.getLastActivity() || Date.now();
    return Date.now() - last >= INACTIVITY_TIMEOUT;
  };

  const refreshAccessToken = async (forceRefresh = false) => {
    if (!forceRefresh && isUserInactive()) {
      await logout();
      return false;
    }

    try {
      const { data } = await api.post('/auth/renovar-token');
      if (data.token) {
        tokenStorage.setAccessToken(data.token, data.expiresIn);
        tokenStorage.touchActivity();
        if (data.expiresIn) {
          scheduleTokenRefresh(data.expiresIn);
        }
        return true;
      }
      return false;
    } catch {
      await logout();
      return false;
    }
  };

  const silentRefreshIfNeeded = async () => {
    if (tokenStorage.getAccessToken()) {
      return true;
    }

    try {
      const { data } = await api.post('/auth/renovar-token');
      if (data.token) {
        tokenStorage.setAccessToken(data.token, data.expiresIn);
        return true;
      }
    } catch {
      return false;
    }
    return false;
  };

  const cargarPerfil = async () => {
    if (!tokenStorage.getAccessToken()) {
      return null;
    }

    try {
      const response = await api.get('/auth/perfil');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await refreshAccessToken(true);
        if (!refreshed) {
          return null;
        }
        const retry = await api.get('/auth/perfil');
        return retry.data;
      }
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    } finally {
      clearRefreshTimer();
      tokenStorage.clear();
      setUser(null);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  };

  useEffect(() => {
    const verificar = async () => {
      if (window.location.pathname === '/login') {
        setCargando(false);
        return;
      }

      const hasSession = await silentRefreshIfNeeded();
      if (!hasSession) {
        tokenStorage.clear();
        setUser(null);
        window.location.href = '/login';
        return;
      }

      const perfil = await cargarPerfil();
      if (perfil) {
        const stored = tokenStorage.getUser();
        const userData = {
          ...(perfil.persona || stored || {}),
          empresa: perfil.empresa || stored?.empresa || null,
          permisos: perfil.permisos || stored?.permisos || [],
          userId: perfil.usuario?.user_id || stored?.userId || null,
          username: perfil.usuario?.username || stored?.username || null,
        };
        tokenStorage.setSession({
          token: tokenStorage.getAccessToken(),
          user: userData,
        });
        setUser(userData);
        tokenStorage.touchActivity();
        scheduleRefreshFromStorage();
      } else {
        await logout();
      }

      setCargando(false);
    };

    verificar();

    const eventos = ['mousemove', 'keydown', 'click', 'scroll'];
    const actualizarActividad = () => tokenStorage.touchActivity();
    eventos.forEach((e) => window.addEventListener(e, actualizarActividad));

    return () => {
      eventos.forEach((e) => window.removeEventListener(e, actualizarActividad));
      clearRefreshTimer();
    };
  }, []);

  const login = async (usuario, contraseña) => {
    try {
      const { data } = await api.post("/auth/login", { usuario, contraseña });

      if (data.token && data.persona) {
        const userData = {
          ...data.persona,
          empresa: data.empresa || null,
          permisos: data.permisos || [],
        };
        tokenStorage.setSession({
          token: data.token,
          user: userData,
          expiresIn: data.expiresIn,
        });
        setUser(userData);

        if (data.expiresIn) {
          scheduleTokenRefresh(data.expiresIn);
        }

        return {
          success: true,
          redirectTo: getFirstAllowedPath(resolvePermissions(userData)),
        };
      }

      return { success: false, message: data.mensaje || "Respuesta de login inválida" };
    } catch (error) {
      let mensajeError = "Error de conexión";
      if (error.response) {
        const payload = error.response.data || {};
        mensajeError = payload.mensaje ||
          payload.message ||
          payload.errors?.usuario?.[0] ||
          payload.errors?.contraseña?.[0] ||
          payload.errors?.auth?.[0] ||
          "Credenciales inválidas";
      }
      return { success: false, message: mensajeError };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        cargando,
        login,
        logout,
        estaAutenticado: tokenStorage.isAuthenticated() && !!user,
        refreshAccessToken,
        updateLastActivity: () => tokenStorage.touchActivity(),
      }}
    >
      {!cargando && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de un AuthProvider");
  }
  return context;
};
